import type { HeroProps } from '@/components/notion/Hero';
import type { HowNotionWorksProps } from '@/components/notion/HowNotionWorks';
import type { WhatIsNotionProps } from '@/components/notion/WhatIsNotion';
import type {
  CustomersReviewsProps,
  HowItWorksProps,
  PricesProps,
  SwitchingProps,
  TrustProps,
} from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import switchingMobile from '@/static/assets/home/bg-switching-mobile.png';
import switching from '@/static/assets/home/bg-switching.png';
import nyse from '@/static/assets/home/nyse.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-NL.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';
import notionBg from '@/static/assets/notion/bg-living-room-combined.svg';
import type { BasePageConfig } from '../../BasePageConfig';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  howNotionWorks: HowNotionWorksProps;
  whatIsNotion: WhatIsNotionProps;
  customersReviews: CustomersReviewsProps;
  prices: PricesProps;
  switching: SwitchingProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

const productJsonld = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Lemonade',
  url: 'https://www.lemonade.com',
  image: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  offers: {
    '@type': 'Offer',
    priceCurrency: 'USD',
    price: '5.00',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.53',
    ratingCount: '10293',
  },
  sameAs: [
    'https://www.facebook.com/Lemonade/',
    'https://twitter.com/lemonade_inc',
    'https://www.instagram.com/lemonade_inc/',
    'https://www.linkedin.com/company/lemonade-inc-',
    'https://www.youtube.com/c/Lemonade_Inc',
    'https://www.pinterest.com/lemonade_inc',
    'https://en.wikipedia.org/wiki/Lemonade,_Inc.',
    'https://www.wikidata.org/wiki/Q37019245',
  ],
};

const agencyJsonld = {
  '@context': 'https://schema.org',
  '@type': 'InsuranceAgency',
  name: 'Lemonade',
  address: '5 Crosby St. 3rd floor, New York, NY 10013 USA',
  image: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  description:
    'Lemonade is a property and casualty insurance company powered by technology and driven by social good. Insuring in the United States, Lemonade offers urban dwellers home, renters and condo insurance policies in 90 seconds, starting from $5/month. No brokers, no paperwork.',
  url: 'https://www.lemonade.com',
  areaServed: 'United States',
};

export const config: Config = {
  seo: { ...sharedConfig['en-US'].seo, jsonld: [agencyJsonld, productJsonld], noIndex: true },
  layout: { ...sharedConfig['en-US'].layout },
  hero: {
    title: "Get America's top-rated \nhome insurance, and then some",
    subtitle: 'Cover your home from $25/month, and get a\nNotion smart monitoring system valued at over $100 for free',
    cta: 'Get Offer',
    videoId: 361223494,
    bgImg: {
      img: notionBg.src,
      position: 'right bottom',
      size: 'auto 40%',
    },
    videoCta: 'About Lemonade',
  },
  howNotionWorks: {
    title: 'How it Works',
    subtitle: 'It’s quick and easy to protect your home \nwith a free security system from Lemonade.',
    steps: {
      first: 'Get a new Lemonade \nHomeowners policy*',
      second: 'After your policy has been active \nfor 30 days, order your Notion',
      third: 'Install devices and watch \nyour smart home protect itself',
    },
    disclaimer: {
      title: 'Terms, Conditions and Legal Stuff',
      paragraph:
        'Offer is only valid for first-time policy purchases. The offer will be valid for 90 days after getting the link to Notion. Limit one gift per insurance policy. The gift is supplied by Notion. Lemonade is not liable for any damage or personal injury, etc. in connection with this offer or accepting or using the gift. Lemonade reserves the right to modify or cancel this offer at any time without notice.',
    },
  },
  whatIsNotion: {
    title: 'What is Notion',
    subtitle: 'A smart home security system that pairs with your phone. \nDid we mention that it’s free?',
    explanation: {
      simple: {
        title: 'SUPER SIMPLE SET UP',
        paragraph: 'Just stick the sensors wherever you need monitoring. And Voila!',
      },
      protection: {
        title: 'ALL AROUND PROTECTION',
        paragraph: 'Alerts you to opening doors and windows, smoke alarms, and water leaks— 24/7.',
      },
      smart: {
        title: 'REALLY SMART DEVICES',
        paragraph: 'Comes with one main bridge and two sensors that sync with your phone.',
      },
    },
  },
  customersReviews: {
    title: 'The (Almost) 5 Star Insurance Company',
    subtitle:
      'Lemonade has earned 4.9 stars in the App store, and\nis also top-rated by Supermoney, Clearsurance, and others',
    darkBackground: true,
  },
  prices: {
    title: 'Incredible Prices,\nMonthly Subscription',
    cardsData: [
      {
        title: 'Renters\nInsurance',
        subtitle: 'MONTHLY, FROM*',
        price: '$5',
        cta: {
          ctaLabel: 'GET RENTERS INSURANCE',
          ctaUrl: '/onboarding/start?p=home_us_renters',
        },
      },
      {
        title: 'Homeowners\nInsurance',
        subtitle: 'MONTHLY, FROM*',
        price: '$25',
        cta: {
          ctaLabel: 'GET HOMEO INSURANCE',
          ctaUrl: '/onboarding/start?p=home_us_owners',
        },
      },
    ],
  },
  switching: {
    title: 'Already insured? We’ll help you switch!',
    subtitle: 'People left these insurance companies to join Lemonade:',
    cta: {
      ctaLabel: 'Check prices and switch',
      ctaUrl: '/onboarding/start?p=home_us',
    },
    showDivider: true,
    compDesktopImgUrl: switching,
    compMobileImgUrl: switchingMobile,
  },
  howItWorks: {
    showBrandsImage: false,
    showTopBorder: true,
    title: 'How Lemonade Works',
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums
        you pay as if it's your money, not ours. With Lemonade, everything
        becomes simple and transparent. We take a flat fee, pay claims super
        fast, and <a href="/giveback">give back</a> what’s left to causes you
        care about.*`,
    disclaimer:
      '* Subject to board discretion and the company meeting certain financial standards. This section does not apply to Life insurance.',
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `If there's money leftover,<br/> we <b>give it back to causes</b>`,
      },
    ],
    darkBackground: false,
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is a publicly traded company, listed on the New York Stock Exchange under the LMND stock symbol',
        image: {
          src: nyse,
          width: 100,
          height: 89,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: `Lemonade is <a href="https://files.lemonade.com/2022+Annual+Statement+with+signed+jurat.pdf" target="_blank"
                        rel="noopener noreferrer">A-rated</a>, fully regulated and reinsured by the most trusted names on the planet`,
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
